// Pure helpers for the Client journeys page: filter state ⇄ URL, the
// PostgREST pieces of the list query, sentiment label, CSV export and the
// "exported by" display name.
//
// Kept free of browser globals (its only import is the pure ./csv helper) so the
// root vitest suite can load it directly — same pattern as notification-categories.ts. The queries in
// queries.ts (useJourneyList / fetchJourneysForCsv) and the page
// (routes/journeys.tsx) are thin wrappers around these.

import { toCsv } from './csv';

export type JourneyTrigger = 'manual' | 'auto_30d_inactivity';
export type JourneyTriggerFilter = 'all' | JourneyTrigger;
export type JourneyPeriod = 'any' | '30d' | '90d' | '12m';
export type JourneySort = 'newest' | 'oldest' | 'most_tickets' | 'client_az';

export interface JourneyFilters {
  /** Free-text search (client email / name / narrative). */
  q: string;
  trigger: JourneyTriggerFilter;
  /** Only journeys exported within this window. */
  period: JourneyPeriod;
  sort: JourneySort;
}

export const DEFAULT_JOURNEY_FILTERS: JourneyFilters = {
  q: '',
  trigger: 'all',
  period: 'any',
  sort: 'newest',
};

/** Rows per "Load more" page. */
export const JOURNEY_PAGE_SIZE = 50;
/** Hard cap on a CSV download, so a huge table can't lock the tab. */
export const JOURNEY_CSV_MAX = 5000;

export const JOURNEY_PERIOD_LABELS: Record<JourneyPeriod, string> = {
  any: 'Any time',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '12m': 'Last 12 months',
};

export const JOURNEY_SORT_OPTIONS: Array<{ key: JourneySort; label: string; hint: string }> = [
  { key: 'newest', label: 'Newest first', hint: 'Most recently exported' },
  { key: 'oldest', label: 'Oldest first', hint: 'Earliest exports first' },
  { key: 'most_tickets', label: 'Most tickets', hint: 'Longest histories first' },
  { key: 'client_az', label: 'Client A–Z', hint: 'By client email' },
];

export const JOURNEY_TRIGGER_LABELS: Record<JourneyTriggerFilter, string> = {
  all: 'All',
  manual: 'Manual',
  auto_30d_inactivity: 'Automatic',
};

const MAX_QUERY_LEN = 80;

// ── URL state ───────────────────────────────────────────────────────────────

type ParamsLike = { get(key: string): string | null };

const TRIGGERS: JourneyTriggerFilter[] = ['all', 'manual', 'auto_30d_inactivity'];
const PERIODS: JourneyPeriod[] = ['any', '30d', '90d', '12m'];
const SORTS: JourneySort[] = ['newest', 'oldest', 'most_tickets', 'client_az'];

/** Read filter state from the URL; anything missing or invalid falls back to
 *  the default so a hand-edited or stale link can never break the page. */
export function parseJourneyFilters(params: ParamsLike): JourneyFilters {
  const trigger = params.get('trigger') as JourneyTriggerFilter | null;
  const period = params.get('period') as JourneyPeriod | null;
  const sort = params.get('sort') as JourneySort | null;
  return {
    q: (params.get('q') ?? '').trim().slice(0, MAX_QUERY_LEN),
    trigger: trigger && TRIGGERS.includes(trigger) ? trigger : DEFAULT_JOURNEY_FILTERS.trigger,
    period: period && PERIODS.includes(period) ? period : DEFAULT_JOURNEY_FILTERS.period,
    sort: sort && SORTS.includes(sort) ? sort : DEFAULT_JOURNEY_FILTERS.sort,
  };
}

/** The non-default filters as URL params (defaults are dropped so a clean
 *  page has a clean URL). */
export function serializeJourneyFilters(f: JourneyFilters): Record<string, string> {
  const out: Record<string, string> = {};
  const q = f.q.trim().slice(0, MAX_QUERY_LEN);
  if (q) out.q = q;
  if (f.trigger !== DEFAULT_JOURNEY_FILTERS.trigger) out.trigger = f.trigger;
  if (f.period !== DEFAULT_JOURNEY_FILTERS.period) out.period = f.period;
  if (f.sort !== DEFAULT_JOURNEY_FILTERS.sort) out.sort = f.sort;
  return out;
}

/** True when any filter differs from the default (drives "Clear filters"). */
export function hasActiveJourneyFilters(f: JourneyFilters): boolean {
  return Object.keys(serializeJourneyFilters(f)).some((k) => k !== 'sort');
}

// ── Query pieces ────────────────────────────────────────────────────────────

/**
 * PostgREST `.or()` expression matching a search term against the client's
 * email, the narrative summary and the client name inside the payload.
 * Returns null for blank input.
 *
 * Characters that are syntax inside `or(...)` values (`, ( ) "`), wildcards the
 * person didn't mean (`* %`) and backslashes are dropped rather than escaped —
 * a search box doesn't need them and getting the escaping wrong 400s the query.
 */
export function searchOrClause(q: string): string | null {
  const term = q
    .replace(/[,()"*%\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_QUERY_LEN);
  if (!term) return null;
  const pat = `*${term}*`;
  return [
    `client_email.ilike.${pat}`,
    `narrative_summary.ilike.${pat}`,
    `payload->>client_name.ilike.${pat}`,
  ].join(',');
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** ISO lower bound on `exported_at` for a period, or undefined for "any". */
export function periodStart(period: JourneyPeriod, now: number): string | undefined {
  switch (period) {
    case '30d':
      return new Date(now - 30 * DAY_MS).toISOString();
    case '90d':
      return new Date(now - 90 * DAY_MS).toISOString();
    case '12m':
      return new Date(now - 365 * DAY_MS).toISOString();
    default:
      return undefined;
  }
}

export function sortSpec(sort: JourneySort): { column: string; ascending: boolean } {
  switch (sort) {
    case 'oldest':
      return { column: 'exported_at', ascending: true };
    case 'most_tickets':
      return { column: 'ticket_count', ascending: false };
    case 'client_az':
      return { column: 'client_email', ascending: true };
    default:
      return { column: 'exported_at', ascending: false };
  }
}

// ── Sentiment ───────────────────────────────────────────────────────────────

export type JourneySentiment = 'positive' | 'neutral' | 'mixed' | 'negative';

/** One word for "how did this client's history feel", from the per-ticket
 *  scores (-1 / 0 / +1) stored in payload.sentiment_series. Null = no data. */
export function sentimentOf(
  series: Array<{ score: number }> | null | undefined,
): JourneySentiment | null {
  const scores = (series ?? []).map((p) => p?.score).filter((s): s is number => typeof s === 'number');
  if (scores.length === 0) return null;
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg > 0.25) return 'positive';
  if (avg < -0.25) return 'negative';
  return scores.every((s) => s === 0) ? 'neutral' : 'mixed';
}

// ── "Exported by" ───────────────────────────────────────────────────────────

/**
 * Display name for `client_journeys.exported_by`, which is `'system'`, a Slack
 * user id, or an email. `resolve` looks a key up (Slack id or lower-cased
 * email) and returns a name, or null if unknown.
 */
export function exporterLabel(
  exportedBy: string | null | undefined,
  resolve: (key: string) => string | null,
): string {
  const raw = (exportedBy ?? '').trim();
  if (!raw) return 'Unknown';
  if (raw === 'system') return 'Automation';
  return resolve(raw) ?? resolve(raw.toLowerCase()) ?? raw;
}

// ── CSV ─────────────────────────────────────────────────────────────────────

export interface JourneyCsvRow {
  client_name: string | null;
  client_email: string;
  ticket_count: number;
  date_range_start: string | null;
  date_range_end: string | null;
  trigger: string;
  exported_by: string;
  exported_at: string;
  narrative_summary: string;
}

const CSV_HEADER = [
  'Client',
  'Email',
  'Tickets',
  'From',
  'To',
  'Trigger',
  'Exported by',
  'Exported at',
  'Summary',
];

/** Excel-friendly CSV: UTF-8 BOM first, CRLF line endings. */
export function journeysToCsv(
  rows: JourneyCsvRow[],
  exporterName: (exportedBy: string) => string,
): string {
  return toCsv(
    CSV_HEADER,
    rows.map((r) => [
      r.client_name ?? '',
      r.client_email,
      r.ticket_count,
      r.date_range_start ? r.date_range_start.slice(0, 10) : '',
      r.date_range_end ? r.date_range_end.slice(0, 10) : '',
      r.trigger === 'manual' ? 'Manual' : r.trigger === 'auto_30d_inactivity' ? 'Automatic' : r.trigger,
      exporterName(r.exported_by),
      r.exported_at,
      r.narrative_summary,
    ]),
  );
}
